import { BreadcrumbBar } from 'features/breadcrumbBar'
import {
  OntologyContextType,
  useOntology,
  useTitle,
} from 'features/dynamicOntology'
import { useRolodexEntries } from 'features/rolodex'
import React, { FC, useMemo } from 'react'
import { createPrismSearchFromEntry } from 'routes/prism/-searchParams'
import { GeckoEntry } from 'types'

type ObjectBreadcrumbsProps = {
  entry: GeckoEntry
  orgId: string | undefined
  siteId: string | undefined
}

const getImmediateParentObjectId = (
  entry: GeckoEntry,
  ontology: OntologyContextType
): string | null => {
  const typeDef = ontology.ontology.types[entry.type]
  const availableParentTypes = typeDef?.requiredTags
  if (!availableParentTypes || availableParentTypes.length === 0) {
    return null
  }

  const immediateParentType = ontology.findLowestType(availableParentTypes)

  if (!immediateParentType) {
    return null
  }

  return entry.tags[immediateParentType as keyof typeof entry.tags] || null
}

export const ObjectBreadcrumbs: FC<ObjectBreadcrumbsProps> = ({
  entry,
  orgId,
  siteId,
}) => {
  const ontology = useOntology()

  const immediateParentId = useMemo(
    () => getImmediateParentObjectId(entry, ontology),
    [entry, ontology]
  )

  const { entries: parentEntries } = useRolodexEntries(
    immediateParentId ? { ids: [immediateParentId] } : undefined
  )

  // NB: We expect only one entry to be present in parentEntries
  const immediateParent = parentEntries.find(
    (parent) => parent.id === immediateParentId
  )

  const currentName = useTitle(entry)
  const parentName = useTitle(immediateParent)

  if (!immediateParentId || !immediateParent) {
    return null
  }

  return (
    <BreadcrumbBar
      border={false}
      lastPage={parentName}
      currentPage={currentName}
      navigateTo='/prism'
      search={createPrismSearchFromEntry(immediateParent, {
        orgId,
        siteId,
      })}
      capitalizePageNames={false} // We're already formatting the names
    />
  )
}
